import { useQuery } from "@tanstack/react-query";
import { FolderOpen, Paperclip, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { rag_v1 } from "@/utils/axios-v2";
import {
  IAutocompleteItem,
  ICollection,
  IMcpTool,
  ITool,
  Tools,
} from "@/types";
import { Button } from "@/components/ui/button";
import { MenuSelection } from "@/pages/Playground/components/agent-tools/menu-list";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Typography } from "@mui/material";

interface SelectedResource {
  id: string;
  name: string;
  type: "tool" | "collection" | "document" | "flow";
  toolId?: string;
}

export interface BlueprintAgentToolsPickerProps {
  selectedTools?: string[];
  selectedCollectionIds?: string[];
  selectedDocumentIds?: string[];
  onChange: (selection: {
    tools: string[];
    collectionIds: string[];
    documentIds: string[];
  }) => void;
}

const isMcpConnection = (tool: ITool | null): boolean => tool?.type === "mcp";

const createInitialResources = (
  selectedTools: string[],
  selectedCollectionIds: string[],
  selectedDocumentIds: string[],
): SelectedResource[] => [
  ...selectedTools.map((id) => ({ id, name: id, type: "tool" as const })),
  ...selectedCollectionIds.map((id) => ({
    id,
    name: id,
    type: "collection" as const,
  })),
  ...selectedDocumentIds.map((id) => ({
    id,
    name: id,
    type: "document" as const,
  })),
];

export default function BlueprintAgentToolsPicker({
  selectedTools = [],
  selectedCollectionIds = [],
  selectedDocumentIds = [],
  onChange,
}: BlueprintAgentToolsPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuType, setMenuType] = useState<"tools" | "collections">("tools");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTool, setSelectedTool] = useState<ITool | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resources, setResources] = useState<SelectedResource[]>(() =>
    createInitialResources(
      selectedTools,
      selectedCollectionIds,
      selectedDocumentIds,
    ),
  );

  const { data: tools = [], isLoading: toolsLoading } = useQuery<Tools>({
    queryKey: ["tools"],
    queryFn: () =>
      rag_v1.get("agent-tools/tools/").then((response) => response.data),
  });

  const { data: autocompleteItems = [], isLoading: autocompleteLoading } =
    useQuery<Array<IAutocompleteItem>>({
      queryKey: ["blueprint-autocomplete", searchQuery],
      queryFn: () =>
        rag_v1
          .get("agent-tools/autocomplete/", { params: { search: searchQuery } })
          .then((response) => response.data || []),
      enabled: isOpen && menuType === "collections" && !selectedTool,
    });

  const { data: collectionItems = [], isLoading: collectionLoading } = useQuery<
    Array<ICollection>
  >({
    queryKey: ["blueprint-autocomplete", selectedTool?.id, searchQuery],
    queryFn: () =>
      rag_v1
        .get(`agent-tools/autocomplete/${selectedTool?.id}`, {
          params: { search: searchQuery },
        })
        .then((response) => response.data || []),
    enabled:
      isOpen &&
      menuType === "collections" &&
      Boolean(selectedTool) &&
      !isMcpConnection(selectedTool),
  });

  const mcpItems = useMemo(() => {
    if (!isMcpConnection(selectedTool)) return [];
    const query = searchQuery.trim().toLowerCase();
    return (selectedTool?.tools || []).filter(
      (item) =>
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query),
    );
  }, [searchQuery, selectedTool]);

  const visibleCollections = selectedTool
    ? selectedTool.type === "mcp"
      ? mcpItems
      : collectionItems
    : autocompleteItems;
  const visibleTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query
      ? tools.filter((tool) => tool.name.toLowerCase().includes(query))
      : tools;
  }, [searchQuery, tools]);
  const isLoading = selectedTool
    ? selectedTool.type === "mcp"
      ? false
      : collectionLoading
    : autocompleteLoading;

  useEffect(() => {
    setSelectedIndex(0);
  }, [menuType, searchQuery, selectedTool, visibleCollections.length]);

  const emitChange = (nextResources: SelectedResource[]) => {
    setResources(nextResources);
    onChange({
      tools: nextResources
        .filter((item) => item.type === "tool")
        .map((item) => item.id),
      collectionIds: nextResources
        .filter((item) => item.type === "collection" || item.type === "flow")
        .map((item) => item.id),
      documentIds: nextResources
        .filter((item) => item.type === "document")
        .map((item) => item.id),
    });
  };

  const addResource = (resource: SelectedResource) => {
    if (
      resources.some(
        (item) => item.id === resource.id && item.type === resource.type,
      )
    )
      return;
    emitChange([...resources, resource]);
  };

  const handleToolSelect = (tool: ITool) => {
    if (tool.has_autocomplete || tool.type === "mcp") {
      setSelectedTool(tool);
      setMenuType("collections");
      setSearchQuery("");
      setSelectedIndex(0);
      return;
    }

    addResource({ id: tool.id, name: tool.name, type: "tool" });
    setIsOpen(false);
  };

  const handleCollectionSelect = (
    item: ICollection | IAutocompleteItem | IMcpTool,
  ) => {
    const type =
      "type" in item && (item.type === "document" || item.type === "flow")
        ? item.type
        : "collection";
    addResource({
      id: item.id,
      name: item.name,
      type,
      toolId: selectedTool?.id,
    });
    setIsOpen(false);
    setMenuType("tools");
    setSelectedTool(null);
    setSearchQuery("");
  };

  const openPicker = () => {
    setMenuType("tools");
    setSelectedTool(null);
    setSearchQuery("");
  };

  const handlePickerOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setMenuType("tools");
      setSelectedTool(null);
      setSearchQuery("");
    }
  };

  const removeResource = (resource: SelectedResource) => {
    emitChange(
      resources.filter(
        (item) => !(item.id === resource.id && item.type === resource.type),
      ),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-2">
          <div>
            <Typography
              variant="body2"
              className="text-foreground text-sm font-semibold"
            >
              Tools
            </Typography>
            <Typography
              variant="body2"
              className="text-sm text-muted-foreground"
            >
              Select the tools, documents and collections
            </Typography>
          </div>
          <div className="relative">
            <DropdownMenu open={isOpen} onOpenChange={handlePickerOpenChange}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={openPicker}
                  aria-label={t("playground.availableTools")}
                  className={`transition-all p-2 cursor-pointer duration-150 rounded-[35px] hover:bg-secondary/50 ${
                    isOpen ? "bg-secondary text-[var(--color-100)]" : ""
                  }`}
                >
                  <FolderOpen className="text-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={8}
                className="w-[min(calc(100vw-2rem),240px)] border-0 bg-transparent p-0 shadow-none"
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <MenuSelection
                  isOpen={isOpen}
                  tools={visibleTools}
                  position={{ left: 0 }}
                  onOpenChange={handlePickerOpenChange}
                  onSelect={handleToolSelect}
                  onCollectionSelect={handleCollectionSelect}
                  menuType={menuType}
                  collections={visibleCollections}
                  isLoading={toolsLoading || isLoading}
                  selectedIndex={selectedIndex}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  selectedTool={selectedTool || undefined}
                  inline
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="flex min-h-10 w-full flex-wrap gap-2 rounded-[8px] border border-border bg-input p-2">
          {resources.length ? (
            resources.map((resource) => (
              <span
                key={`${resource.type}-${resource.id}`}
                className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-foreground"
              >
                {resource.name}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-4 rounded-full p-0"
                  onClick={() => removeResource(resource)}
                  aria-label={`Remove ${resource.name}`}
                >
                  <X className="size-3" />
                </Button>
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("playground.noToolsSelected", "No tools selected")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
